import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import OrientationLinkPanel from "@/components/OrientationLinkPanel";
import EmployeeOrientationTab from "@/components/EmployeeOrientationTab";
import AssignInserviceDialog from "@/components/AssignInserviceDialog";
import BackgroundCheckPanel from "@/components/BackgroundCheckPanel";
import EmployeeFilesTab from "@/components/EmployeeFilesTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  Shield,
  Calendar,
  Users,
  Clock,
  UserPlus,
  User,
  Save,
  MapPin,
  FileText,
  Briefcase,
  CreditCard,
  AlertCircle,
  Plus,
  GraduationCap,
  DollarSign,
  MessageSquare,
  Ban,
  Download,
  FolderOpen,
  List,
  CalendarDays,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import { PersonalInfoTab, ExclusionsTab } from "@/components/employee-detail";
import EmployeePermissionsPanel from "@/components/EmployeePermissionsPanel";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const payrollItemOptions = [
  { value: "hourly_personal_care", label: "Hourly Personal Care" },
  { value: "hourly_travel_time", label: "Hourly Travel Time" },
  { value: "hourly_supportive_care", label: "Hourly Supportive Care" },
  { value: "regular_hours", label: "Regular Hours" },
  { value: "overtime", label: "Overtime" },
  { value: "holiday_pay", label: "Holiday Pay" },
  { value: "sick_leave", label: "Sick Leave" },
  { value: "vacation", label: "Vacation" },
  { value: "training", label: "Training" },
  { value: "bonus", label: "Bonus" },
  { value: "mileage", label: "Mileage Reimbursement" },
  { value: "other", label: "Other" },
];

const roleLabels = {
  admin: "Administrator",
  case_manager: "Case Manager",
  rn: "Registered Nurse",
  pcw: "Personal Care Worker",
};

const roleOptions = [
  { value: "admin", label: "Administrator" },
  { value: "case_manager", label: "Case Manager" },
  { value: "rn", label: "Registered Nurse" },
  { value: "pcw", label: "Personal Care Worker" },
];

const roleBadgeClasses = {
  admin: "role-admin",
  case_manager: "role-case_manager",
  rn: "role-rn",
  pcw: "role-pcw",
};

const daysOfWeek = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canDownloadDocs = hasPermission("documents", "download");
  const [orientationRefreshKey, setOrientationRefreshKey] = useState(0);
  const [assignInserviceOpen, setAssignInserviceOpen] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [linkedUserId, setLinkedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignedClients, setAssignedClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [assignClientDialogOpen, setAssignClientDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [editingPersonalData, setEditingPersonalData] = useState(false);
  const [savingPersonalData, setSavingPersonalData] = useState(false);
  
  // Inservices state
  const [inservices, setInservices] = useState([]);
  const [inserviceDialogOpen, setInserviceDialogOpen] = useState(false);
  const [newInservice, setNewInservice] = useState({
    name: "",
    date_completed: "",
    credit: "",
  });
  const [savingInservice, setSavingInservice] = useState(false);
  
  // Payroll Items state
  const [payrollItems, setPayrollItems] = useState([]);
  const [payrollItemDialogOpen, setPayrollItemDialogOpen] = useState(false);
  const [editingPayrollItemId, setEditingPayrollItemId] = useState(null);
  const [newPayrollItem, setNewPayrollItem] = useState({
    item_name: "",
    rate: "",
    effective_date: "",
    notes: "",
  });
  const [savingPayrollItem, setSavingPayrollItem] = useState(false);
  
  // Notes state
  const [employeeNotes, setEmployeeNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [savingNote, setSavingNote] = useState(false);
  
  // Exclusions state
  const [exclusions, setExclusions] = useState([]);
  const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);
  const [selectedExclusionClientId, setSelectedExclusionClientId] = useState("");
  const [exclusionReason, setExclusionReason] = useState("");
  const [savingExclusion, setSavingExclusion] = useState(false);
  
  const [personalData, setPersonalData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    date_of_birth: "",
    ssn_last4: "",
    hire_date: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    license_number: "",
    license_expiry: "",
    certifications: "",
    evv_number: "",
    pay_rate: "",
    pay_type: "hourly",
    assessment_visit_rate: "",
    supervisory_visit_rate: "",
    documentation_incentive_rate: "",
    bank_account_last4: "",
    notes: "",
  });
  const [schedule, setSchedule] = useState({
    monday: { available: false, start: "09:00", end: "17:00" },
    tuesday: { available: false, start: "09:00", end: "17:00" },
    wednesday: { available: false, start: "09:00", end: "17:00" },
    thursday: { available: false, start: "09:00", end: "17:00" },
    friday: { available: false, start: "09:00", end: "17:00" },
    saturday: { available: false, start: "09:00", end: "17:00" },
    sunday: { available: false, start: "09:00", end: "17:00" },
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [assigningClient, setAssigningClient] = useState(false);
  
  // Employee appointments state
  const [employeeAppointments, setEmployeeAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [scheduleViewMode, setScheduleViewMode] = useState("calendar");

  useEffect(() => {
    fetchEmployee();
    fetchAssignedClients();
    fetchAllClients();
    fetchEmployeeAppointments();
  }, [id]);

  const fetchEmployeeAppointments = async () => {
    setLoadingAppointments(true);
    try {
      // Get all appointments for this employee (no limit for calendar view)
      const response = await axios.get(`${API}/appointments?employee_id=${id}`);
      // Sort by date
      const sorted = response.data.sort((a, b) => 
        new Date(a.scheduled_date) - new Date(b.scheduled_date)
      );
      setEmployeeAppointments(sorted);
    } catch (error) {
      console.error("Failed to fetch employee appointments:", error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}`);
      setEmployee(response.data);
      if (response.data.schedule) {
        setSchedule(response.data.schedule);
      }
      // Set inservices, payroll items, notes, exclusions
      setInservices(response.data.inservices || []);
      setPayrollItems(response.data.payroll_items || []);
      setEmployeeNotes(response.data.employee_notes || []);
      setExclusions(response.data.exclusions || []);

      // Look up linked user account (by email) so admins can edit permissions
      if (response.data.email && currentUser?.role === "admin") {
        try {
          const usersRes = await axios.get(`${API}/users`);
          const list = usersRes.data?.users || [];
          const match = list.find(
            (u) => (u.email || "").toLowerCase() === response.data.email.toLowerCase()
          );
          setLinkedUserId(match?.id || null);
        } catch (err) {
          console.warn("Could not look up linked user account", err);
        }
      }
      // Set personal data from employee
      setPersonalData({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        role: response.data.role || "",
        address: response.data.address || "",
        city: response.data.city || "",
        state: response.data.state || "",
        zip_code: response.data.zip_code || "",
        date_of_birth: response.data.date_of_birth || "",
        ssn_last4: response.data.ssn_last4 || "",
        hire_date: response.data.hire_date || "",
        emergency_contact_name: response.data.emergency_contact_name || "",
        emergency_contact_phone: response.data.emergency_contact_phone || "",
        emergency_contact_relation: response.data.emergency_contact_relation || "",
        license_number: response.data.license_number || "",
        license_expiry: response.data.license_expiry || "",
        certifications: response.data.certifications || "",
        evv_number: response.data.evv_number || "",
        pay_rate: response.data.pay_rate?.toString() || "",
        pay_type: response.data.pay_type || "hourly",
        assessment_visit_rate: response.data.assessment_visit_rate?.toString() || "",
        supervisory_visit_rate: response.data.supervisory_visit_rate?.toString() || "",
        documentation_incentive_rate: response.data.documentation_incentive_rate?.toString() || "",
        bank_account_last4: response.data.bank_account_last4 || "",
        notes: response.data.notes || "",
      });
    } catch (error) {
      console.error("Failed to fetch employee:", error);
      toast.error("Failed to load employee details");
      navigate("/employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedClients = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}/clients`);
      setAssignedClients(response.data);
    } catch (error) {
      console.error("Failed to fetch assigned clients:", error);
    }
  };

  const fetchAllClients = async () => {
    try {
      const response = await axios.get(`${API}/clients?status=active`);
      setAllClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const handleUpdateSchedule = async () => {
    setSavingSchedule(true);
    try {
      await axios.patch(`${API}/employees/${id}`, { schedule });
      toast.success("Schedule updated!");
      setScheduleDialogOpen(false);
      fetchEmployee();
    } catch (error) {
      console.error("Failed to update schedule:", error);
      toast.error("Failed to update schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleAssignClient = async () => {
    if (!selectedClientId) {
      toast.error("Please select a client");
      return;
    }

    setAssigningClient(true);
    try {
      await axios.post(`${API}/employees/${id}/assign-client`, {
        client_id: selectedClientId,
      });
      toast.success("Client assigned!");
      setAssignClientDialogOpen(false);
      setSelectedClientId("");
      fetchAssignedClients();
    } catch (error) {
      console.error("Failed to assign client:", error);
      toast.error(error.response?.data?.detail || "Failed to assign client");
    } finally {
      setAssigningClient(false);
    }
  };

  const handleUnassignClient = async (clientId) => {
    try {
      await axios.delete(`${API}/employees/${id}/unassign-client/${clientId}`);
      toast.success("Client unassigned");
      fetchAssignedClients();
    } catch (error) {
      console.error("Failed to unassign client:", error);
      toast.error("Failed to unassign client");
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Employee deleted");
      navigate("/employees");
    } catch (error) {
      console.error("Failed to delete employee:", error);
      toast.error("Failed to delete employee");
    }
  };

  const handleToggleStatus = async () => {
    try {
      await axios.patch(`${API}/employees/${id}`, {
        is_active: !employee.is_active,
      });
      toast.success(employee.is_active ? "Employee deactivated" : "Employee activated");
      fetchEmployee();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleSavePersonalData = async () => {
    if (!personalData.name || !personalData.email) {
      toast.error("Name and email are required");
      return;
    }

    setSavingPersonalData(true);
    try {
      await axios.patch(`${API}/employees/${id}`, {
        ...personalData,
        pay_rate: personalData.pay_rate ? parseFloat(personalData.pay_rate) : null,
        assessment_visit_rate: personalData.assessment_visit_rate ? parseFloat(personalData.assessment_visit_rate) : null,
        supervisory_visit_rate: personalData.supervisory_visit_rate ? parseFloat(personalData.supervisory_visit_rate) : null,
        documentation_incentive_rate: personalData.documentation_incentive_rate ? parseFloat(personalData.documentation_incentive_rate) : null,
      });
      toast.success("Personal data updated!");
      setEditingPersonalData(false);
      fetchEmployee();
    } catch (error) {
      console.error("Failed to update personal data:", error);
      toast.error(error.response?.data?.detail || "Failed to update personal data");
    } finally {
      setSavingPersonalData(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPersonalData(false);
    // Reset to original data
    setPersonalData({
      name: employee.name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      role: employee.role || "",
      address: employee.address || "",
      city: employee.city || "",
      state: employee.state || "",
      zip_code: employee.zip_code || "",
      date_of_birth: employee.date_of_birth || "",
      ssn_last4: employee.ssn_last4 || "",
      hire_date: employee.hire_date || "",
      emergency_contact_name: employee.emergency_contact_name || "",
      emergency_contact_phone: employee.emergency_contact_phone || "",
      emergency_contact_relation: employee.emergency_contact_relation || "",
      license_number: employee.license_number || "",
      license_expiry: employee.license_expiry || "",
      certifications: employee.certifications || "",
      evv_number: employee.evv_number || "",
      pay_rate: employee.pay_rate?.toString() || "",
      pay_type: employee.pay_type || "hourly",
      assessment_visit_rate: employee.assessment_visit_rate?.toString() || "",
      supervisory_visit_rate: employee.supervisory_visit_rate?.toString() || "",
      documentation_incentive_rate: employee.documentation_incentive_rate?.toString() || "",
      bank_account_last4: employee.bank_account_last4 || "",
      notes: employee.notes || "",
    });
  };

  // Inservice handlers
  const handleAddInservice = async () => {
    if (!newInservice.name || !newInservice.date_completed) {
      toast.error("Name and date completed are required");
      return;
    }
    setSavingInservice(true);
    try {
      const updatedInservices = [...inservices, {
        ...newInservice,
        id: Date.now().toString(),
        created_at: new Date().toISOString(),
      }];
      await axios.patch(`${API}/employees/${id}`, { inservices: updatedInservices });
      setInservices(updatedInservices);
      setInserviceDialogOpen(false);
      setNewInservice({ name: "", date_completed: "", credit: "" });
      toast.success("Inservice added");
    } catch (error) {
      toast.error("Failed to add inservice");
    } finally {
      setSavingInservice(false);
    }
  };

  const handleDeleteInservice = async (inserviceId) => {
    try {
      const updatedInservices = inservices.filter(i => i.id !== inserviceId);
      await axios.patch(`${API}/employees/${id}`, { inservices: updatedInservices });
      setInservices(updatedInservices);
      toast.success("Inservice deleted");
    } catch (error) {
      toast.error("Failed to delete inservice");
    }
  };

  // Payroll Item handlers
  const handleAddPayrollItem = async () => {
    if (!newPayrollItem.item_name || !newPayrollItem.rate) {
      toast.error("Item name and rate are required");
      return;
    }
    setSavingPayrollItem(true);
    try {
      let updatedItems;
      if (editingPayrollItemId) {
        // Update existing item
        updatedItems = payrollItems.map(item => 
          item.id === editingPayrollItemId 
            ? { 
                ...item, 
                ...newPayrollItem, 
                rate: parseFloat(newPayrollItem.rate),
                updated_at: new Date().toISOString() 
              }
            : item
        );
        toast.success("Payroll item updated");
      } else {
        // Add new item
        updatedItems = [...payrollItems, {
          ...newPayrollItem,
          id: Date.now().toString(),
          rate: parseFloat(newPayrollItem.rate),
          created_at: new Date().toISOString(),
        }];
        toast.success("Payroll item added");
      }
      await axios.patch(`${API}/employees/${id}`, { payroll_items: updatedItems });
      setPayrollItems(updatedItems);
      setPayrollItemDialogOpen(false);
      setEditingPayrollItemId(null);
      setNewPayrollItem({ item_name: "", rate: "", effective_date: "", notes: "" });
    } catch (error) {
      toast.error(editingPayrollItemId ? "Failed to update payroll item" : "Failed to add payroll item");
    } finally {
      setSavingPayrollItem(false);
    }
  };

  const handleEditPayrollItem = (item) => {
    setEditingPayrollItemId(item.id);
    setNewPayrollItem({
      item_name: item.item_name || "",
      rate: item.rate?.toString() || "",
      effective_date: item.effective_date || "",
      notes: item.notes || "",
    });
    setPayrollItemDialogOpen(true);
  };

  const handleDeletePayrollItem = async (itemId) => {
    try {
      const updatedItems = payrollItems.filter(i => i.id !== itemId);
      await axios.patch(`${API}/employees/${id}`, { payroll_items: updatedItems });
      setPayrollItems(updatedItems);
      toast.success("Payroll item deleted");
    } catch (error) {
      toast.error("Failed to delete payroll item");
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Note cannot be empty");
      return;
    }
    setSavingNote(true);
    try {
      const note = {
        id: Date.now().toString(),
        content: newNote,
        type: noteType,
        created_at: new Date().toISOString(),
        author: "Current User", // Would come from auth context
      };
      const updatedNotes = [...employeeNotes, note];
      await axios.patch(`${API}/employees/${id}`, { employee_notes: updatedNotes });
      setEmployeeNotes(updatedNotes);
      setNewNote("");
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const updatedNotes = employeeNotes.filter(n => n.id !== noteId);
      await axios.patch(`${API}/employees/${id}`, { employee_notes: updatedNotes });
      setEmployeeNotes(updatedNotes);
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  // Exclusion handlers
  const handleAddExclusion = async () => {
    if (!selectedExclusionClientId) {
      toast.error("Please select a client");
      return;
    }
    setSavingExclusion(true);
    try {
      const client = allClients.find(c => c.id === selectedExclusionClientId);
      const exclusion = {
        id: Date.now().toString(),
        client_id: selectedExclusionClientId,
        client_name: client ? `${client.first_name} ${client.last_name}` : "Unknown",
        reason: exclusionReason,
        created_at: new Date().toISOString(),
      };
      const updatedExclusions = [...exclusions, exclusion];
      await axios.patch(`${API}/employees/${id}`, { exclusions: updatedExclusions });
      setExclusions(updatedExclusions);
      setExclusionDialogOpen(false);
      setSelectedExclusionClientId("");
      setExclusionReason("");
      toast.success("Exclusion added");
    } catch (error) {
      toast.error("Failed to add exclusion");
    } finally {
      setSavingExclusion(false);
    }
  };

  const handleDeleteExclusion = async (exclusionId) => {
    try {
      const updatedExclusions = exclusions.filter(e => e.id !== exclusionId);
      await axios.patch(`${API}/employees/${id}`, { exclusions: updatedExclusions });
      setExclusions(updatedExclusions);
      toast.success("Exclusion removed");
    } catch (error) {
      toast.error("Failed to remove exclusion");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  if (!employee) {
    return null;
  }

  const unassignedClients = allClients.filter(
    (client) => !assignedClients.find((ac) => ac.id === client.id)
  );

  return (
    <Layout>
      {/* Back Button */}
      <Link
        to="/employees"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6 transition-colors"
        data-testid="back-to-employees"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Employees
      </Link>

      {/* Employee Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xl flex-shrink-0">
              {employee.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-slate-900">{employee.name}</h1>
                <span className={`badge ${roleBadgeClasses[employee.role] || ""}`}>
                  {roleLabels[employee.role] || employee.role}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-slate-600">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {employee.email}
                </span>
                {employee.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {employee.phone}
                  </span>
                )}
              </div>
              {employee.license_number && (
                <p className="text-sm text-slate-500 mt-2">
                  License: {employee.license_number}
                  {employee.license_expiry && ` (Exp: ${employee.license_expiry})`}
                </p>
              )}
              {employee.evv_number && (
                <p className="text-sm text-slate-500 mt-1">
                  EVV #: {employee.evv_number}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <OrientationLinkPanel
              employeeId={employee.id}
              employeeName={employee.name}
              isAdmin={currentUser?.role === "admin"}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAssignInserviceOpen(true)}
              className="border-teal-200 text-teal-700 hover:bg-teal-50"
              data-testid="assign-inservice-btn"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Assign in-service
            </Button>
            <AssignInserviceDialog
              open={assignInserviceOpen}
              onClose={() => setAssignInserviceOpen(false)}
              employees={[{ id: employee.id, name: employee.name, role: employee.role }]}
              preselected={[employee.id]}
              onAssigned={() => setOrientationRefreshKey((k) => k + 1)}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleStatus}
              data-testid="toggle-status-btn"
            >
              {employee.is_active ? "Deactivate" : "Activate"}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Employee?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove {employee.name} from your agency. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700"
                    onClick={handleDeleteEmployee}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <span
            className={`badge ${employee.is_active ? "badge-active" : "badge-inactive"}`}
          >
            {employee.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="personal" data-testid="tab-personal">
            <User className="w-4 h-4 mr-2" />
            Personal Data
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <Clock className="w-4 h-4 mr-2" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Calendar className="w-4 h-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">
            <Users className="w-4 h-4 mr-2" />
            Assigned Clients
          </TabsTrigger>
          <TabsTrigger value="inservices" data-testid="tab-inservices">
            <GraduationCap className="w-4 h-4 mr-2" />
            Inservices
          </TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">
            <DollarSign className="w-4 h-4 mr-2" />
            Payroll Items
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <MessageSquare className="w-4 h-4 mr-2" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">
            <FolderOpen className="w-4 h-4 mr-2" />
            Files
          </TabsTrigger>
          <TabsTrigger value="exclusions" data-testid="tab-exclusions">
            <Ban className="w-4 h-4 mr-2" />
            Exclusions
          </TabsTrigger>
          <TabsTrigger value="orientation" data-testid="tab-orientation">
            <GraduationCap className="w-4 h-4 mr-2" />
            Orientation
          </TabsTrigger>
          {currentUser?.role === "admin" && (
            <TabsTrigger value="permissions" data-testid="tab-permissions">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Permissions
            </TabsTrigger>
          )}
        </TabsList>


        {/* Personal Data Tab - Using extracted component */}
        <TabsContent value="personal">
          <PersonalInfoTab
            employee={employee}
            editingPersonalData={editingPersonalData}
            setEditingPersonalData={setEditingPersonalData}
            personalData={personalData}
            setPersonalData={setPersonalData}
            savingPersonalData={savingPersonalData}
            handleSavePersonalData={handleSavePersonalData}
            handleCancelEdit={handleCancelEdit}
          />
        </TabsContent>


        {/* Schedule Tab */}
        <TabsContent value="schedule">
          {/* Appointments Section with Calendar/List Toggle */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Scheduled Appointments ({employeeAppointments.filter(a => a.status === 'scheduled').length})
                </h3>
                <p className="text-sm text-slate-500">All visits assigned to this employee</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center border rounded-lg overflow-hidden">
                  <Button
                    size="sm"
                    variant={scheduleViewMode === "calendar" ? "default" : "ghost"}
                    onClick={() => setScheduleViewMode("calendar")}
                    className="rounded-none gap-1"
                    data-testid="emp-calendar-view-btn"
                  >
                    <CalendarDays className="w-4 h-4" />
                    Calendar
                  </Button>
                  <Button
                    size="sm"
                    variant={scheduleViewMode === "list" ? "default" : "ghost"}
                    onClick={() => setScheduleViewMode("list")}
                    className="rounded-none gap-1"
                    data-testid="emp-list-view-btn"
                  >
                    <List className="w-4 h-4" />
                    List
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchEmployeeAppointments}
                  className="gap-2"
                  data-testid="refresh-appointments-btn"
                >
                  <Clock className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>

            {loadingAppointments ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50 animate-pulse" />
                <p className="text-sm">Loading appointments...</p>
              </div>
            ) : employeeAppointments.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No appointments assigned</p>
                <p className="text-xs mt-1">Appointments from Service Orders will appear here</p>
              </div>
            ) : scheduleViewMode === "calendar" ? (
              /* Calendar View */
              <ScheduleCalendar 
                appointments={employeeAppointments}
                loading={loadingAppointments}
                showClientName={true}
                showEmployeeName={false}
                onAppointmentCancelled={() => fetchEmployeeAppointments()}
              />
            ) : (
              /* List View */
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {employeeAppointments
                  .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date))
                  .map((appt) => (
                  <div
                    key={appt.id}
                    className={`p-4 border rounded-lg transition-colors ${
                      appt.status === 'completed'
                        ? 'bg-emerald-50 border-emerald-200'
                        : appt.status === 'cancelled'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-white border-slate-200 hover:border-primary/50'
                    }`}
                    data-testid={`employee-appt-${appt.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            appt.status === 'scheduled'
                              ? 'bg-blue-100 text-blue-700'
                              : appt.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {appt.status?.charAt(0).toUpperCase() + appt.status?.slice(1)}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {appt.appointment_type === 'visit' ? 'Home Visit' : appt.appointment_type}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-700">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {appt.scheduled_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-slate-400" />
                            {appt.scheduled_time}
                          </span>
                          {appt.duration_minutes && (
                            <span className="text-slate-500">
                              ({Math.floor(appt.duration_minutes / 60)}h {appt.duration_minutes % 60}m)
                            </span>
                          )}
                        </div>

                        <Link 
                          to={`/clients/${appt.client_id}`}
                          className="text-sm text-primary hover:underline mt-2 flex items-center gap-1"
                        >
                          <User className="w-4 h-4" />
                          {appt.client_name}
                        </Link>

                        {appt.notes && (
                          <p className="text-xs text-slate-500 mt-2 italic border-l-2 border-slate-200 pl-2">
                            {appt.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Weekly Availability Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">Weekly Availability</h3>
              <Button
                size="sm"
                onClick={() => setScheduleDialogOpen(true)}
                className="gap-2"
                data-testid="edit-schedule-btn"
              >
                <Edit className="w-4 h-4" />
                Edit Availability
              </Button>
            </div>

            <div className="grid gap-3">
              {daysOfWeek.map((day) => {
                const daySchedule = schedule[day.value];
                return (
                  <div
                    key={day.value}
                    className={`p-4 rounded-lg border ${
                      daySchedule?.available
                        ? "border-primary/30 bg-primary/5"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            daySchedule?.available ? "bg-primary" : "bg-slate-300"
                          }`}
                        />
                        <span className="font-medium text-slate-800">{day.label}</span>
                      </div>
                      {daySchedule?.available ? (
                        <span className="text-sm text-slate-600 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {daySchedule.start} - {daySchedule.end}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">Not Available</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Calendar Tab - Dedicated calendar view for employee */}
        <TabsContent value="calendar">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Employee Calendar
                </h3>
                <p className="text-sm text-slate-500">
                  All scheduled visits for {employee?.name}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    {employeeAppointments.filter(a => a.status === 'scheduled').length} Scheduled
                  </span>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                    {employeeAppointments.filter(a => a.status === 'completed').length} Completed
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchEmployeeAppointments}
                  className="gap-2"
                >
                  <Clock className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Assigned Clients Summary */}
            {assignedClients.length > 0 && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Assigned Clients ({assignedClients.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {assignedClients.slice(0, 6).map((client) => (
                    <Link
                      key={client.id}
                      to={`/clients/${client.id}`}
                      className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:border-primary/50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-sm font-medium">
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        {client.first_name} {client.last_name}
                      </span>
                    </Link>
                  ))}
                  {assignedClients.length > 6 && (
                    <span className="px-3 py-2 text-sm text-slate-500">
                      +{assignedClients.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {loadingAppointments ? (
              <div className="flex items-center justify-center h-64">
                <Clock className="w-8 h-8 animate-pulse text-slate-400" />
              </div>
            ) : (
              <ScheduleCalendar
                appointments={employeeAppointments}
                loading={loadingAppointments}
                showClientName={true}
                showEmployeeName={false}
                onAppointmentCancelled={() => fetchEmployeeAppointments()}
              />
            )}
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                Assigned Clients ({assignedClients.length})
              </h3>
              <Button
                size="sm"
                onClick={() => setAssignClientDialogOpen(true)}
                className="gap-2"
                data-testid="assign-client-btn"
              >
                <UserPlus className="w-4 h-4" />
                Assign Client
              </Button>
            </div>

            {assignedClients.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No clients assigned</p>
                <p className="text-xs mt-1">Click &quot;Assign Client&quot; to assign active clients</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assignedClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg"
                  >
                    <Link
                      to={`/clients/${client.id}`}
                      className="flex items-center gap-3 hover:text-primary"
                    >
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-medium">
                        {client.first_name[0]}
                        {client.last_name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{client.phone || "No phone"}</p>
                      </div>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnassignClient(client.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Unassign
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Inservices Tab */}
        <TabsContent value="inservices">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                Inservices ({inservices.length})
              </h3>
              <Button
                size="sm"
                onClick={() => setInserviceDialogOpen(true)}
                className="gap-2"
                data-testid="add-inservice-btn"
              >
                <Plus className="w-4 h-4" />
                Add Inservice
              </Button>
            </div>

            {inservices.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No inservices recorded</p>
                <p className="text-xs mt-1">Click &quot;Add Inservice&quot; to add training records</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Date Completed</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Credit</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inservices.map((inservice) => (
                      <tr key={inservice.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-slate-800">{inservice.name}</td>
                        <td className="py-3 px-4 text-slate-600">{inservice.date_completed}</td>
                        <td className="py-3 px-4 text-slate-600">{inservice.credit || "-"}</td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteInservice(inservice.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Payroll Items Tab */}
        <TabsContent value="payroll">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                Payroll Items ({payrollItems.length})
              </h3>
              <Button
                size="sm"
                onClick={() => {
                  setEditingPayrollItemId(null);
                  setNewPayrollItem({ item_name: "", rate: "", effective_date: "", notes: "" });
                  setPayrollItemDialogOpen(true);
                }}
                className="gap-2"
                data-testid="add-payroll-item-btn"
              >
                <Plus className="w-4 h-4" />
                Add Payroll Item
              </Button>
            </div>

            {payrollItems.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No payroll items configured</p>
                <p className="text-xs mt-1">Click &quot;Add Payroll Item&quot; to set up pay rates</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Item Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Rate</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Effective Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Notes</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollItems.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-slate-800">
                          {payrollItemOptions.find(o => o.value === item.item_name)?.label || item.item_name}
                        </td>
                        <td className="py-3 px-4 text-slate-600">${parseFloat(item.rate).toFixed(2)}</td>
                        <td className="py-3 px-4 text-slate-600">{item.effective_date || "-"}</td>
                        <td className="py-3 px-4 text-slate-600 max-w-xs truncate">{item.notes || "-"}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPayrollItem(item)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`edit-payroll-item-${item.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePayrollItem(item.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-payroll-item-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                Employee Notes ({employeeNotes.length})
              </h3>
            </div>

            {/* Add Note Form */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex gap-4 mb-3">
                <Select value={noteType} onValueChange={setNoteType}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="incident">Incident</SelectItem>
                    <SelectItem value="recognition">Recognition</SelectItem>
                    <SelectItem value="hr">HR Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder="Add a note about this employee..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                className="mb-3"
                data-testid="employee-note-input"
              />
              <Button
                onClick={handleAddNote}
                disabled={savingNote || !newNote.trim()}
                className="gap-2"
                data-testid="add-note-btn"
              >
                <Plus className="w-4 h-4" />
                {savingNote ? "Adding..." : "Add Note"}
              </Button>
            </div>

            {employeeNotes.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notes yet</p>
                <p className="text-xs mt-1">Add notes to keep track of important information</p>
              </div>
            ) : (
              <div className="space-y-4">
                {employeeNotes.slice().reverse().map((note) => (
                  <div key={note.id} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          note.type === 'incident' ? 'bg-red-100 text-red-700' :
                          note.type === 'recognition' ? 'bg-green-100 text-green-700' :
                          note.type === 'performance' ? 'bg-blue-100 text-blue-700' :
                          note.type === 'hr' ? 'bg-purple-100 text-purple-700' :
                          note.type === 'orientation' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {note.type === 'orientation' ? 'Orientation Packet' : (note.type?.charAt(0).toUpperCase() + note.type?.slice(1) || 'General')}
                        </span>
                        <span className="text-xs text-slate-500">
                          {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      {note.author !== 'System' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-700 -mt-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-slate-800 whitespace-pre-wrap">{note.content}</p>
                    {note.attachment_path && (
                      <div className="mt-3 flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                        <FileText className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-emerald-900 truncate">
                            {note.attachment_filename || 'Attachment'}
                          </p>
                        </div>
                        {canDownloadDocs && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          onClick={async () => {
                            try {
                              const res = await fetch(
                                `${API}/employees/${id}/notes/${note.id}/attachment`,
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } }
                              );
                              if (!res.ok) throw new Error();
                              const blob = await res.blob();
                              const link = document.createElement('a');
                              link.href = URL.createObjectURL(blob);
                              link.download = note.attachment_filename || 'attachment.pdf';
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                            } catch {
                              toast.error('Download failed');
                            }
                          }}
                          data-testid={`download-note-attachment-${note.id}`}
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </Button>
                        )}
                      </div>
                    )}
                    {note.author && (
                      <p className="text-xs text-slate-500 mt-2">— {note.author}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Exclusions Tab - Using extracted component */}
        <TabsContent value="exclusions">
          <ExclusionsTab
            exclusions={exclusions}
            onAddExclusion={() => setExclusionDialogOpen(true)}
            onDeleteExclusion={handleDeleteExclusion}
          />
        </TabsContent>

        {/* Orientation Tab - Full history + packet download */}
        <TabsContent value="orientation" className="space-y-6">
          <BackgroundCheckPanel
            employeeId={employee.id}
            isAdmin={currentUser?.role === "admin"}
            onCleared={() => setOrientationRefreshKey((k) => k + 1)}
          />
          <EmployeeOrientationTab
            key={orientationRefreshKey}
            employeeId={employee.id}
            employeeName={employee.name || "Employee"}
          />
        </TabsContent>

        {/* Files Tab - HR document vault */}
        <TabsContent value="files">
          <EmployeeFilesTab
            employeeId={employee.id}
            isAdmin={currentUser?.role === "admin"}
          />
        </TabsContent>

        {/* Permissions Tab - Per-employee overrides on top of role baseline */}
        {currentUser?.role === "admin" && (
          <TabsContent value="permissions" className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Employee Permissions</h3>
                <p className="text-sm text-slate-500">
                  Grant or revoke individual features for this employee on top of their role&apos;s baseline.
                </p>
              </div>
              {linkedUserId ? (
                <EmployeePermissionsPanel userId={linkedUserId} canEdit />
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">
                  <ShieldCheck className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="font-medium">No portal account linked</p>
                  <p className="mt-1 text-slate-500">
                    This employee doesn&apos;t have a login yet. Invite them under
                    <span className="font-medium"> Settings → Users</span> to grant permissions.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Schedule</DialogTitle>
            <DialogDescription>
              Set the weekly availability for {employee.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            {daysOfWeek.map((day) => (
              <div key={day.value} className="space-y-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`day-${day.value}`}
                    checked={schedule[day.value]?.available || false}
                    onCheckedChange={(checked) =>
                      setSchedule((prev) => ({
                        ...prev,
                        [day.value]: { ...prev[day.value], available: checked },
                      }))
                    }
                  />
                  <Label htmlFor={`day-${day.value}`} className="font-medium">
                    {day.label}
                  </Label>
                </div>
                {schedule[day.value]?.available && (
                  <div className="flex items-center gap-2 ml-7">
                    <input
                      type="time"
                      value={schedule[day.value]?.start || "09:00"}
                      onChange={(e) =>
                        setSchedule((prev) => ({
                          ...prev,
                          [day.value]: { ...prev[day.value], start: e.target.value },
                        }))
                      }
                      className="px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                    <span className="text-slate-400">to</span>
                    <input
                      type="time"
                      value={schedule[day.value]?.end || "17:00"}
                      onChange={(e) =>
                        setSchedule((prev) => ({
                          ...prev,
                          [day.value]: { ...prev[day.value], end: e.target.value },
                        }))
                      }
                      className="px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSchedule} disabled={savingSchedule}>
              {savingSchedule ? "Saving..." : "Save Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Client Dialog */}
      <Dialog open={assignClientDialogOpen} onOpenChange={setAssignClientDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Client</DialogTitle>
            <DialogDescription>
              Select an active client to assign to {employee.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {unassignedClients.length === 0 ? (
              <div className="text-center py-4 text-slate-500">
                <p className="text-sm">No available clients to assign</p>
                <p className="text-xs mt-1">All active clients are already assigned</p>
              </div>
            ) : (
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {unassignedClients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.first_name} {client.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignClientDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignClient}
              disabled={assigningClient || !selectedClientId}
            >
              {assigningClient ? "Assigning..." : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Inservice Dialog */}
      <Dialog open={inserviceDialogOpen} onOpenChange={setInserviceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Inservice</DialogTitle>
            <DialogDescription>
              Record a training or inservice for {employee.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inservice-name">Name *</Label>
              <Input
                id="inservice-name"
                value={newInservice.name}
                onChange={(e) => setNewInservice(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., CPR Training, Safety Protocol"
                data-testid="inservice-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inservice-date">Date Completed *</Label>
              <Input
                id="inservice-date"
                type="date"
                value={newInservice.date_completed}
                onChange={(e) => setNewInservice(prev => ({ ...prev, date_completed: e.target.value }))}
                data-testid="inservice-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inservice-credit">Credit</Label>
              <Input
                id="inservice-credit"
                value={newInservice.credit}
                onChange={(e) => setNewInservice(prev => ({ ...prev, credit: e.target.value }))}
                placeholder="e.g., 2 hours, 1 CEU"
                data-testid="inservice-credit-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInserviceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddInservice}
              disabled={savingInservice || !newInservice.name || !newInservice.date_completed}
              data-testid="save-inservice-btn"
            >
              {savingInservice ? "Saving..." : "Add Inservice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payroll Item Dialog */}
      <Dialog open={payrollItemDialogOpen} onOpenChange={(open) => {
        setPayrollItemDialogOpen(open);
        if (!open) {
          setEditingPayrollItemId(null);
          setNewPayrollItem({ item_name: "", rate: "", effective_date: "", notes: "" });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPayrollItemId ? "Edit Payroll Item" : "Add Payroll Item"}</DialogTitle>
            <DialogDescription>
              {editingPayrollItemId ? "Update" : "Configure"} a payroll rate for {employee.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="payroll-item-name">Item Name *</Label>
              <Select
                value={newPayrollItem.item_name}
                onValueChange={(value) => setNewPayrollItem(prev => ({ ...prev, item_name: value }))}
              >
                <SelectTrigger data-testid="payroll-item-name-select">
                  <SelectValue placeholder="Select payroll item" />
                </SelectTrigger>
                <SelectContent>
                  {payrollItemOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payroll-rate">Rate *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="payroll-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newPayrollItem.rate}
                  onChange={(e) => setNewPayrollItem(prev => ({ ...prev, rate: e.target.value }))}
                  placeholder="0.00"
                  className="pl-7"
                  data-testid="payroll-rate-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payroll-effective-date">Effective Date</Label>
              <Input
                id="payroll-effective-date"
                type="date"
                value={newPayrollItem.effective_date}
                onChange={(e) => setNewPayrollItem(prev => ({ ...prev, effective_date: e.target.value }))}
                data-testid="payroll-effective-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payroll-notes">Notes</Label>
              <Input
                id="payroll-notes"
                value={newPayrollItem.notes}
                onChange={(e) => setNewPayrollItem(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes..."
                data-testid="payroll-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayrollItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPayrollItem}
              disabled={savingPayrollItem || !newPayrollItem.item_name || !newPayrollItem.rate}
              data-testid="save-payroll-item-btn"
            >
              {savingPayrollItem ? "Saving..." : editingPayrollItemId ? "Update Payroll Item" : "Add Payroll Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Exclusion Dialog */}
      <Dialog open={exclusionDialogOpen} onOpenChange={setExclusionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Client Exclusion</DialogTitle>
            <DialogDescription>
              Select a client that {employee.name} cannot work with
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="exclusion-client">Client *</Label>
              <Select value={selectedExclusionClientId} onValueChange={setSelectedExclusionClientId}>
                <SelectTrigger data-testid="exclusion-client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {allClients
                    .filter(c => !exclusions.find(e => e.client_id === c.id))
                    .map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="exclusion-reason">Reason (optional)</Label>
              <Textarea
                id="exclusion-reason"
                value={exclusionReason}
                onChange={(e) => setExclusionReason(e.target.value)}
                placeholder="Why can't this employee work with this client?"
                rows={3}
                data-testid="exclusion-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExclusionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddExclusion}
              disabled={savingExclusion || !selectedExclusionClientId}
              data-testid="save-exclusion-btn"
            >
              {savingExclusion ? "Saving..." : "Add Exclusion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
